export type Tier = 'standard' | 'deep';

export type PublicConfig = {
  price_standard_cents: string;
  price_deep_cents: string;
  xianyu_url_standard: string;
  xianyu_url_deep: string;
};

export type Order = {
  id: string;
  public_code: string;
  tier: Tier | string;
  status: string;
  amount_cents: number;
  xianyu_order_no: string | null;
  created_at: string;
  updated_at: string;
  xianyu_url?: string;
};

export type Job = {
  id: string;
  order_id: string;
  status: string;
  attempt: number;
  error: string | null;
  provider_meta: unknown;
  created_at: string;
  updated_at: string;
};

export type Asset = {
  id: string;
  kind: string;
  preview_url: string;
  created_at: string;
};

async function readJson<T>(r: Response): Promise<T> {
  if (!r.ok) {
    let detail = '';
    try {
      const body = (await r.json()) as { error?: string };
      detail = body.error ? `: ${body.error}` : '';
    } catch {
      /* ignore */
    }
    throw new Error(`http_${r.status}${detail}`);
  }
  return r.json() as Promise<T>;
}

export async function getPublicConfig(): Promise<PublicConfig> {
  const r = await fetch('/api/config/public');
  return readJson<PublicConfig>(r);
}

export async function createOrder(tier: Tier): Promise<Order> {
  const r = await fetch('/api/orders', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ tier }),
  });
  if (!r.ok) throw new Error('create_failed');
  return r.json() as Promise<Order>;
}

export async function getOrder(publicCode: string): Promise<Order> {
  const r = await fetch(`/api/orders/${encodeURIComponent(publicCode)}`);
  return readJson<Order>(r);
}

export async function submitXianyu(
  publicCode: string,
  xianyu_order_no: string,
): Promise<{ ok: boolean; status: string; xianyu_order_no: string }> {
  const r = await fetch(`/api/orders/${encodeURIComponent(publicCode)}/xianyu`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ xianyu_order_no }),
  });
  return readJson(r);
}

export async function uploadSource(
  publicCode: string,
  file: File,
): Promise<{ asset_id: string; preview_url: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`/api/orders/${encodeURIComponent(publicCode)}/assets/source`, {
    method: 'POST',
    body: fd,
  });
  return readJson(r);
}

export async function createJob(
  publicCode: string,
  body: { style?: unknown; face?: unknown } = {},
): Promise<Job> {
  const r = await fetch(`/api/orders/${encodeURIComponent(publicCode)}/jobs`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return readJson<Job>(r);
}

export async function listJobs(publicCode: string): Promise<{ jobs: Job[] }> {
  const r = await fetch(`/api/orders/${encodeURIComponent(publicCode)}/jobs`);
  return readJson(r);
}

export async function listAssets(publicCode: string): Promise<{ assets: Asset[] }> {
  const r = await fetch(`/api/orders/${encodeURIComponent(publicCode)}/assets`);
  return readJson(r);
}

export function formatYuan(cents: number | string): string {
  const n = typeof cents === 'string' ? Number(cents) : cents;
  if (!Number.isFinite(n)) return '—';
  return `¥${(n / 100).toFixed(2)}`;
}

/** UI fallback when public config is unavailable or price fields are empty. */
export const PRICE_FALLBACK_CENTS = {
  standard: 1990,
  deep: 9990,
} as const;

/** Resolve display cents: prefer live config, else known defaults (never blank). */
export function resolvePriceCents(
  cfg: PublicConfig | null | undefined,
  key: 'standard' | 'deep',
): number {
  const raw = key === 'standard' ? cfg?.price_standard_cents : cfg?.price_deep_cents;
  if (raw == null || raw === '') return PRICE_FALLBACK_CENTS[key];
  const n = typeof raw === 'string' ? Number(raw) : raw;
  if (!Number.isFinite(n) || n <= 0) return PRICE_FALLBACK_CENTS[key];
  return n;
}

export const ORDER_STATUS_LABEL: Record<string, string> = {
  pending_payment: '待支付',
  pending_confirm: '待确认',
  paid: '已支付',
  rejected: '已拒绝',
};

export const JOB_STATUS_LABEL: Record<string, string> = {
  queued: '排队中',
  succeeded: '已完成',
  failed: '失败',
};

export type AdminOrder = Order & {
  admin_note: string | null;
};

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function readJsonCred<T>(r: Response): Promise<T> {
  if (!r.ok) {
    let detail = '';
    try {
      const body = (await r.json()) as { error?: string };
      detail = body.error ? `: ${body.error}` : '';
    } catch {
      /* ignore */
    }
    throw new ApiError(r.status, `http_${r.status}${detail}`);
  }
  return r.json() as Promise<T>;
}

const cred: RequestInit = { credentials: 'include' };

export async function adminLogin(username: string, password: string): Promise<void> {
  const r = await fetch('/api/admin/login', {
    ...cred,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  await readJsonCred<{ ok: boolean }>(r);
}

export async function adminListOrders(): Promise<{ orders: AdminOrder[] }> {
  const r = await fetch('/api/admin/orders', cred);
  return readJsonCred(r);
}

export async function adminGetOrder(
  id: string,
): Promise<{ order: AdminOrder; jobs: Job[] }> {
  const r = await fetch(`/api/admin/orders/${encodeURIComponent(id)}`, cred);
  return readJsonCred(r);
}

export async function adminConfirmOrder(id: string): Promise<{ status: string }> {
  const r = await fetch(`/api/admin/orders/${encodeURIComponent(id)}/confirm`, {
    ...cred,
    method: 'POST',
  });
  return readJsonCred(r);
}

export async function adminRejectOrder(
  id: string,
  note?: string,
): Promise<{ status: string }> {
  const r = await fetch(`/api/admin/orders/${encodeURIComponent(id)}/reject`, {
    ...cred,
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ note: note || undefined }),
  });
  return readJsonCred(r);
}

export async function adminRetryJob(
  id: string,
): Promise<{ status: string; attempt: number }> {
  const r = await fetch(`/api/admin/jobs/${encodeURIComponent(id)}/retry`, {
    ...cred,
    method: 'POST',
  });
  return readJsonCred(r);
}

export async function adminGetConfig(): Promise<PublicConfig> {
  const r = await fetch('/api/admin/config', cred);
  return readJsonCred(r);
}

export async function adminPutConfig(
  body: Partial<PublicConfig>,
): Promise<PublicConfig> {
  const r = await fetch('/api/admin/config', {
    ...cred,
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return readJsonCred(r);
}

export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

export type MeUser = {
  id: string;
  login_key: string;
  login_type: 'phone' | 'username' | 'email';
  display_name: string | null;
};


export async function register(body: {
  identifier: string; password: string; display_name?: string;
}): Promise<MeUser> {
  const r = await fetch('/api/auth/register', {
    credentials: 'include', method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return readJsonCred<MeUser>(r);
}

export async function login(identifier: string, password: string): Promise<MeUser> {
  const r = await fetch('/api/auth/login', {
    credentials: 'include', method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ identifier, password }),
  });
  return readJsonCred<MeUser>(r);
}

export async function getMe(): Promise<MeUser | null> {
  const r = await fetch('/api/auth/me', { credentials: 'include' });
  if (r.status === 401 || !r.ok) return null;
  return r.json() as Promise<MeUser>;
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
}

export async function listMyOrders(): Promise<{ orders: Order[] }> {
  const r = await fetch('/api/me/orders', { credentials: 'include' });
  return readJsonCred(r);
}

export async function adminListUsers(q?: string): Promise<{
  users: Array<{
    id: string; login_key: string; login_type: string;
    display_name: string | null; status: string; created_at: string;
  }>;
}> {
  const url = q ? `/api/admin/users?q=${encodeURIComponent(q)}` : '/api/admin/users';
  const r = await fetch(url, { credentials: 'include' });
  return readJsonCred(r);
}

export async function adminResetPassword(id: string, password: string): Promise<void> {
  const r = await fetch(`/api/admin/users/${encodeURIComponent(id)}/reset-password`, {
    credentials: 'include',
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  await readJsonCred(r);
}
